/**
 * Loop RANGE bundle (BYOB qty-breaks) — Wild Olive Oil.
 *
 * Sells N x single-bottle variant as a Loop RANGE bundle:
 *   Read Bundle Details -> create transaction -> sectioned /cart/add.js with
 *   `_bundleId` -> POST /cart/update.js {discount: <tier codes>} (authoritative set).
 * The tier discount is a Shopify discount CODE (one per tier, created by Loop);
 * it applies AT CHECKOUT, not in the cart display.
 *
 * Also owns the cart guard: when a range-bundle line's qty changes in the
 * drawer, it re-creates the Loop transaction and re-registers the right tier
 * code (otherwise a stale cheaper/richer tier would ride along).
 *
 * Cart contract (CART-ARCHITECTURE.md): every write carries `sections` and is
 * followed by a `cart:update` dispatch; the guard tags its own dispatches and
 * ignores them to avoid self-loops.
 */
(function () {
  'use strict';

  const API = 'https://api.loopsubscriptions.com/storefront/2023-10';
  const CACHE_KEY = 'loopRangeBundle:details:';
  const SNAP_KEY = 'loopRangeBundle:snapshot';
  // v2: bumped so tabs that persisted under the old key (which recorded the GET
  // as success before knowing the rewrite outcome) re-read as dirty and re-heal.
  const CODES_KEY = 'loopRangeBundle:codes:v2';
  const CACHE_TTL_MS = 10 * 60 * 1000;
  const SOURCE = 'loop-range-bundle';
  const RETIER_SOURCE = 'loop-range-bundle-retier';

  let details = null; // resolved RANGE bundle details (single range bundle per store)
  const fixedDetails = {}; // FIXED-box bundles (Polyphenols+/Greek Pack), keyed by nano id
  let retierTimer = null;
  let retierRetries = 0; // bounded retry budget for transient rewrite failures
  // Generation counter: bumped by every user action (stepper click, cart event).
  // A retier cycle aborts before writing when a newer action superseded it, so
  // the short debounce can't interleave guard writes with the user's own.
  let retierGen = 0;
  let txnTimer = null; // slow-lane (txn metadata sync) idle timer
  let lastIntent = null; // {qty, at} — captured at the user's last stepper action

  function sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.getAttribute('data-section-id'))
      .filter(Boolean);
  }

  async function fetchDetails(bundleId) {
    const key = CACHE_KEY + bundleId;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key));
      if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.data;
    } catch (e) {
      /* ignore bad cache */
    }
    const res = await fetch(API + '/bundle/' + encodeURIComponent(bundleId));
    const json = await res.json();
    if (!json || !json.data) throw new Error('Loop bundle details unavailable');
    const d = json.data;
    const product = d.products && d.products[0];
    const variant = product && product.variants && product.variants[0];
    const data = {
      bundleId: bundleId,
      boxSizeId: d.boxSizes && d.boxSizes[0] && d.boxSizes[0].id,
      bundleName: d.label || d.name,
      variantId: variant && variant.shopifyId,
      productId: product && product.shopifyId,
      sellingPlanShopifyId: d.sellingPlans && d.sellingPlans[0] && d.sellingPlans[0].shopifyId,
      // sorted ascending by min quantity; keep purchaseType to filter per mode
      tiers: (d.discounts || [])
        .filter((t) => t.conditionType === 'QUANTITY')
        .map((t) => ({
          id: t.id,
          code: t.code,
          value: t.value,
          min: t.minCartQuantity,
          purchaseType: t.purchaseType,
        }))
        .sort((a, b) => a.min - b.min),
    };
    if (!data.variantId || !data.boxSizeId) throw new Error('Loop bundle details incomplete');
    try {
      sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), data: data }));
    } catch (e) {
      /* storage full — run uncached */
    }
    return data;
  }

  function tierFrom(tiers, qty, subscribe) {
    const wanted = subscribe ? 'SUBSCRIPTION' : 'ONETIME';
    // Prefer tiers scoped to the purchase type; 'ALL' matches both. Fall back to
    // any tier so a Loop config without ONETIME rows still resolves (0-discount rows included).
    let pool = tiers.filter((t) => t.purchaseType === wanted || t.purchaseType === 'ALL');
    if (!pool.length) pool = tiers;
    let best = null;
    for (const t of pool) if (qty >= t.min) best = t;
    return best;
  }

  function tierFor(qty, subscribe) {
    if (!details) return null;
    return tierFrom(details.tiers, qty, subscribe);
  }

  /* ---------- FIXED-box bundles (Polyphenols+/Greek Pack) ----------
   * A fixed box sells ALL the bundle's products together (1 x each per box) in
   * one Loop transaction + the min-quantity tier code. Money is safe by
   * construction: the tier code's minimum is validated by Shopify at checkout,
   * so no client-side price guard is needed. What IS enforced here:
   *   - one authoritative owner of the cart's discount-code set (registerCodes
   *     below covers range AND fixed lines — two writers would clobber each other);
   *   - all-or-nothing boxes (orphan sweep in retier): removing one child would
   *     otherwise recreate the contract-vs-paid mismatch class on subscriptions.
   */
  async function fetchFixedDetails(bundleId) {
    const key = 'loopFixedBundle:details:' + bundleId;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key));
      if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.data;
    } catch (e) {
      /* ignore bad cache */
    }
    const res = await fetch(API + '/bundle/' + encodeURIComponent(bundleId));
    const json = await res.json();
    if (!json || !json.data) throw new Error('Loop bundle details unavailable');
    const d = json.data;
    const data = {
      bundleId: bundleId,
      boxSizeId: d.boxSizes && d.boxSizes[0] && d.boxSizes[0].id,
      bundleName: d.label || d.name,
      sellingPlanShopifyId: d.sellingPlans && d.sellingPlans[0] && d.sellingPlans[0].shopifyId,
      // one child line per product, first variant each — a box is 1 x each
      items: (d.products || []).map((p) => ({
        productId: p.shopifyId,
        variantId: p.variants && p.variants[0] && p.variants[0].shopifyId,
      })),
      tiers: (d.discounts || [])
        .filter((t) => t.conditionType === 'QUANTITY')
        .map((t) => ({ id: t.id, code: t.code, value: t.value, min: t.minCartQuantity, purchaseType: t.purchaseType }))
        .sort((a, b) => a.min - b.min),
    };
    if (!data.boxSizeId || !data.items.length || data.items.some((i) => !i.variantId)) {
      throw new Error('Loop bundle details incomplete');
    }
    try {
      sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), data: data }));
    } catch (e) {
      /* storage full — run uncached */
    }
    return data;
  }

  async function initFixed(bundleId) {
    if (!fixedDetails[bundleId]) fixedDetails[bundleId] = await fetchFixedDetails(bundleId);
    return fixedDetails[bundleId];
  }

  function fixedLines(cart, bundleId) {
    return cart.items.filter(
      (i) =>
        i.properties &&
        i.properties._loopFixedBundleNanoId &&
        (!bundleId || i.properties._loopFixedBundleNanoId === bundleId)
    );
  }

  async function createFixedTransaction(d, boxes, tier, subscribe) {
    const body = {
      bundleId: d.bundleId,
      boxSizeId: d.boxSizeId,
      quantity: 1,
      productVariants: d.items.map((i) => ({ shopifyId: i.variantId, quantity: boxes })),
    };
    if (tier) body.discountId = tier.id;
    if (subscribe) body.sellingPlanShopifyId = d.sellingPlanShopifyId;
    const res = await fetch(API + '/bundle/' + encodeURIComponent(d.bundleId) + '/transaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    const txnId = json && json.data && json.data.txnId;
    if (!txnId) throw new Error('Loop transaction failed');
    return txnId;
  }

  async function createTransaction(qty, tier, subscribe) {
    const body = {
      bundleId: details.bundleId,
      boxSizeId: details.boxSizeId,
      quantity: 1,
      productVariants: [{ shopifyId: details.variantId, quantity: qty }],
    };
    if (tier) body.discountId = tier.id;
    if (subscribe) body.sellingPlanShopifyId = details.sellingPlanShopifyId;
    const res = await fetch(API + '/bundle/' + encodeURIComponent(details.bundleId) + '/transaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    const txnId = json && json.data && json.data.txnId;
    if (!txnId) throw new Error('Loop transaction failed');
    return txnId;
  }

  async function getCart() {
    return (await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })).json();
  }

  function bundleLines(cart) {
    if (!details) return [];
    return cart.items.filter(
      (i) =>
        i.properties &&
        i.properties._bundleId &&
        i.properties._loopRangeBundleNanoId === details.bundleId &&
        i.variant_id === details.variantId
    );
  }

  async function registerCodes(cart) {
    // One tier code per range-bundle line (sub and one-time lines can coexist);
    // with no bundle lines left, the 0% tier code neutralizes any leftover tier.
    const codes = [];
    for (const line of bundleLines(cart)) {
      const t = tierFor(line.quantity, !!line.selling_plan_allocation);
      if (t && t.code && !codes.includes(t.code)) codes.push(t.code);
    }
    // Fixed boxes: one code per bundle+mode, tier resolved on that bundle's own
    // total item count (2 per box on the 2-product bundles).
    for (const nano in fixedDetails) {
      const d = fixedDetails[nano];
      for (const sub of [true, false]) {
        const lines = fixedLines(cart, nano).filter((l) => !!l.selling_plan_allocation === sub);
        if (!lines.length) continue;
        const totalItems = lines.reduce((s, l) => s + l.quantity, 0);
        const t = tierFrom(d.tiers, totalItems, sub);
        if (t && t.code && !codes.includes(t.code)) codes.push(t.code);
      }
    }
    const mustHave = codes.slice(); // tier codes backing lines actually in THIS cart
    if (!codes.length && details) {
      const zero = details.tiers.find((t) => t.value === 0);
      if (zero) codes.push(zero.code);
    }
    // Preserve REAL, currently-applicable promo codes — the `discount` field
    // below replaces the whole code set. Only cart.discount_codes qualifies:
    // allocation/application titles include AUTOMATIC discounts (e.g. the GWP
    // "Free ExtraVirgin Oil"), and echoing those back pollutes discount_codes
    // with a permanent inapplicable entry.
    const keep = [];
    for (const d of cart.discount_codes || []) {
      if (d && d.code && d.applicable !== false && !/^LPBNDL/i.test(d.code) && !keep.includes(d.code)) keep.push(d.code);
    }
    const want = keep.concat(codes).join(',');

    // Skip the write ONLY when our last registration matches AND the cart
    // REALLY carries every tier code: a third-party write (GWP auto-apply,
    // checkout upsell) can evict codes while `want` stays identical, and the
    // sessionStorage comparison alone reads that drift as "already done"
    // (root cause of the 23 Jul drawer-$184.68 / checkout-$205.20 mismatch).
    const applied = (cart.discount_codes || []).map((d) => String(d.code || '').toUpperCase());
    const inSync = mustHave.every((c) => applied.indexOf(c.toUpperCase()) !== -1);
    if (inSync && sessionStorage.getItem(CODES_KEY) === want) return { codes: codes, dirty: false, sections: null };

    // Authoritative registration: POST /cart/update.js {discount} — the same
    // native call the theme's cart-discount component uses. Unlike the legacy
    // GET /discount/ cookie, this REPLACES the applied code set, RE-EVALUATES
    // immediately (even a code pinned by a previously opened checkout — verified
    // 17 Jul), and returns fresh sections to dispatch. Persist only on success
    // so a failed registration retries on the next cycle/load.
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: want, sections: sectionIds().join(','), sections_url: window.location.pathname }),
      });
      if (res.ok) {
        const data = await res.json();
        try {
          sessionStorage.setItem(CODES_KEY, want);
        } catch (e) {
          /* ignore */
        }
        return { codes: codes, dirty: true, sections: data.sections || null };
      }
      console.error('[LoopRangeBundle] discount registration rejected', res.status);
    } catch (e) {
      /* non-fatal: checkout can still take the code manually */
    }
    return { codes: codes, dirty: true, sections: null };
  }

  // Snapshot is keyed by purchase MODE, not by line.key: our own property rewrite
  // changes the Shopify line key (it hashes the properties), which would orphan a
  // key-based snapshot. The merge-on-add invariant guarantees at most one bundle
  // line per mode, so the mode is a stable identity. Plan flips are handled
  // atomically by flipPlan(), so the guard only needs to catch qty changes.
  function modeKey(line) {
    return line.selling_plan_allocation ? 'sub' : 'ot';
  }

  function snapshot(cart) {
    const snap = {};
    for (const line of bundleLines(cart)) snap[modeKey(line)] = line.quantity;
    try {
      sessionStorage.setItem(SNAP_KEY, JSON.stringify(snap));
    } catch (e) {
      /* ignore */
    }
  }

  function readSnapshot() {
    try {
      return JSON.parse(sessionStorage.getItem(SNAP_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function dispatchCartUpdate(sections, source) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: source,
          data: { source: source, sections: sections || {}, itemCount: 1 },
        },
      })
    );
  }

  async function init(bundleId) {
    if (!details || details.bundleId !== bundleId) details = await fetchDetails(bundleId);
    return details;
  }

  /**
   * Add qty bottles as a range bundle. Merges into an existing bundle line with
   * the same purchase mode (one line per mode = one unambiguous tier per line).
   * Returns the sections payload from the cart response.
   */
  async function addToCart(opts) {
    await init(opts.bundleId);
    const qty = Math.max(1, Number(opts.qty) || 1);
    const subscribe = !!opts.subscribe;
    const cart = await getCart();
    const existing = bundleLines(cart).find((l) => !!l.selling_plan_allocation === subscribe);
    const totalQty = qty + (existing ? existing.quantity : 0);
    const tier = tierFor(totalQty, subscribe);
    const txnId = await createTransaction(totalQty, tier, subscribe);
    const sections = sectionIds().join(',');

    let response;
    if (existing) {
      response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: existing.key,
          quantity: totalQty,
          properties: Object.assign({}, existing.properties, { _bundleId: txnId }),
          sections: sections,
          sections_url: window.location.pathname,
        }),
      });
    } else {
      const item = {
        id: details.variantId,
        quantity: totalQty,
        properties: {
          _bundleId: txnId,
          _loopRangeBundleNanoId: details.bundleId,
          bundleName: details.bundleName,
        },
      };
      if (subscribe) item.selling_plan = details.sellingPlanShopifyId;
      response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item], sections: sections }),
      });
    }
    if (!response.ok) {
      let msg = 'Could not add bundle to cart';
      try {
        const err = await response.json();
        msg = err.description || err.message || msg;
      } catch (e) {
        /* keep default */
      }
      throw new Error(msg);
    }
    const result = await response.json();
    const freshCart = await getCart();
    const reg = await registerCodes(freshCart);
    snapshot(freshCart);
    // Prefer the registration's sections: they render AFTER the tier code was
    // applied, so the drawer opens on the correct price right away.
    dispatchCartUpdate(reg.sections || result.sections, SOURCE);
    return reg.sections || result.sections;
  }

  /**
   * Add a FIXED box (all bundle products, 1 x each per box). Merges into an
   * existing box of the same purchase mode by bumping every child to the new
   * box count under a fresh Loop transaction.
   */
  async function addFixedToCart(opts) {
    const d = await initFixed(opts.bundleId);
    const boxes = Math.max(1, Number(opts.boxes) || 1);
    const subscribe = !!opts.subscribe;
    const cart = await getCart();
    const mine = fixedLines(cart, d.bundleId).filter((l) => !!l.selling_plan_allocation === subscribe);
    const prevBoxes = mine.length ? Math.max.apply(null, mine.map((l) => l.quantity)) : 0;
    const totalBoxes = prevBoxes + boxes;
    const tier = tierFrom(d.tiers, totalBoxes * d.items.length, subscribe);
    const txnId = await createFixedTransaction(d, totalBoxes, tier, subscribe);
    const sections = sectionIds().join(',');

    if (mine.length) {
      // Line keys rotate on every write (F10) — re-resolve each child fresh.
      for (const item of d.items) {
        const fresh = fixedLines(await getCart(), d.bundleId).find(
          (l) => l.variant_id === item.variantId && !!l.selling_plan_allocation === subscribe
        );
        if (!fresh) continue; // missing sibling — the orphan sweep reconciles
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: fresh.key,
            quantity: totalBoxes,
            properties: Object.assign({}, fresh.properties, { _bundleId: txnId }),
            sections: sections,
            sections_url: window.location.pathname,
          }),
        });
        if (!res.ok) throw new Error('Could not update bundle in cart');
      }
    } else {
      const items = d.items.map((i) => {
        const it = {
          id: i.variantId,
          quantity: totalBoxes,
          properties: {
            _bundleId: txnId,
            _loopFixedBundleNanoId: d.bundleId,
            bundleName: d.bundleName,
          },
        };
        if (subscribe) it.selling_plan = d.sellingPlanShopifyId;
        return it;
      });
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items, sections: sections }),
      });
      if (!res.ok) {
        let msg = 'Could not add bundle to cart';
        try {
          const err = await res.json();
          msg = err.description || err.message || msg;
        } catch (e) {
          /* keep default */
        }
        throw new Error(msg);
      }
    }
    const freshCart = await getCart();
    const reg = await registerCodes(freshCart);
    dispatchCartUpdate(reg.sections || null, SOURCE);
    return reg.sections;
  }

  /**
   * Flip a range-bundle line between subscription and one-time in ONE atomic
   * /cart/change.js: fresh Loop transaction for the target state + selling plan +
   * new _bundleId + sections. Called by cart-sub-upsell-toggle.js (mirrors its
   * LoopBundleCart delegation for preset lines). Returns the cart response.
   */
  async function flipPlan(opts) {
    await init(opts.properties._loopRangeBundleNanoId);
    const subscribe = !!opts.sellingPlanId;
    const tier = tierFor(opts.quantity, subscribe);
    const txnId = await createTransaction(opts.quantity, tier, subscribe);
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: opts.key,
        quantity: opts.quantity,
        selling_plan: subscribe ? Number(opts.sellingPlanId) : '',
        properties: Object.assign({}, opts.properties, { _bundleId: txnId }),
        sections: (opts.sectionIds || sectionIds()).join(','),
        sections_url: window.location.pathname,
      }),
    });
    const cart = await res.json();
    if (res.ok && !cart.errors && !cart.status) {
      registerCodes(cart).catch(() => {});
      snapshot(cart);
    }
    return cart;
  }

  /**
   * Cart guard, two lanes.
   * FAST (this function, ~150ms after a cart event): register the right tier
   * codes via /cart/update.js {discount} — fixes price + checkout money, and by
   * construction can never touch a quantity.
   * SLOW (syncTxns, after >=1.2s of user quiet): refresh the Loop txn metadata
   * (_bundleId) with a /cart/change.js property rewrite. Deferred because that
   * write must carry `quantity` (omitting it resets the line to 1!) and a stale
   * quantity racing the user's own stepper would UNDO their click.
   */
  async function retier(seedCart, gen) {
    const g = gen == null ? ++retierGen : gen;
    // seedCart = the cart JSON already carried by the native cart:update event
    // (the /cart/change.js response) — saves a /cart.js roundtrip on the hot
    // stepper path. Callers without one (boot heal, retry) fall back to a fetch.
    const cart = seedCart && Array.isArray(seedCart.items) ? seedCart : await getCart();
    if (g !== retierGen) return; // a newer user action owns the cart now

    // Fixed-box bundles present in the cart must have their details loaded
    // BEFORE registerCodes, or their tier code would be dropped from the set.
    for (const i of cart.items) {
      const n = i.properties && i.properties._loopFixedBundleNanoId;
      if (n && !fixedDetails[n]) await initFixed(n).catch(() => {});
    }
    if (!details && !Object.keys(fixedDetails).length) return;

    const snap = readSnapshot();
    const lines = bundleLines(cart);
    const changed = lines.filter((l) => snap[modeKey(l)] !== l.quantity);

    const reg = await registerCodes(cart);
    // The registration write already re-evaluated the cart and returned fresh
    // sections — dispatch them so the drawer shows the new tier price NOW.
    if (reg.sections && g === retierGen) dispatchCartUpdate(reg.sections, RETIER_SOURCE);

    // All-or-nothing sweep for fixed boxes: a box whose txn group lost a child
    // (drawer remove) must not survive partially — on subscriptions the Loop
    // contract is built from the FULL box txn, so a partial cart line-set would
    // recreate the contract-vs-paid mismatch class. Remove the leftovers.
    // ponytail: leftovers re-resolved per delete via fresh cart (keys rotate);
    // 2-product boxes leave at most one leftover line per group anyway.
    const groups = {};
    for (const l of fixedLines(cart)) {
      const k = l.properties._bundleId || 'none';
      (groups[k] = groups[k] || []).push(l);
    }
    let swept = false;
    for (const k in groups) {
      const d = fixedDetails[groups[k][0].properties._loopFixedBundleNanoId];
      if (!d || groups[k].length >= d.items.length) continue;
      for (const stale of groups[k]) {
        if (g !== retierGen) return;
        const fresh = fixedLines(await getCart()).find(
          (l) => l.properties._bundleId === k && l.variant_id === stale.variant_id
        );
        if (!fresh) continue;
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: fresh.key,
            quantity: 0,
            sections: sectionIds().join(','),
            sections_url: window.location.pathname,
          }),
        }).catch(() => {});
        swept = true;
      }
    }
    if (swept && g === retierGen) {
      const after = await getCart();
      const r2 = await registerCodes(after);
      dispatchCartUpdate(r2.sections, RETIER_SOURCE);
    }

    if (changed.length || reg.dirty) scheduleTxnSync(g);
  }

  function scheduleTxnSync(g) {
    clearTimeout(txnTimer);
    txnTimer = setTimeout(() => {
      if (g === retierGen) syncTxns(g).catch((e) => console.error('[LoopRangeBundle] txn sync', e));
    }, 1200);
  }

  async function syncTxns(g) {
    if (!details || g !== retierGen) return;
    const cart = await getCart();
    if (g !== retierGen) return;
    const snap = readSnapshot();
    const lines = bundleLines(cart);
    // Missing snapshot entry (new tab / heal) or mismatch (qty changed, or
    // poisoned -1 after a failure) -> that line's txn needs a refresh.
    const toSync = lines.filter((l) => snap[modeKey(l)] !== l.quantity);
    if (!toSync.length) {
      retierRetries = 0;
      return;
    }
    const sections = sectionIds().join(',');
    let failed = false;
    const failedModes = [];
    for (const line of toSync) {
      if (g !== retierGen) return;
      const subscribe = !!line.selling_plan_allocation;
      const tier = tierFor(line.quantity, subscribe);
      try {
        const txnId = await createTransaction(line.quantity, tier, subscribe);
        if (g !== retierGen) return;
        // Line keys ROTATE on every cart write and discount registration —
        // resolve the line by purchase MODE from a fresh fetch (F10).
        const fresh = bundleLines(await getCart()).find((l) => modeKey(l) === modeKey(line));
        if (!fresh) continue;
        if (g !== retierGen) return;
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: fresh.key,
            quantity: fresh.quantity,
            properties: Object.assign({}, fresh.properties, { _bundleId: txnId }),
            sections: sections,
            sections_url: window.location.pathname,
          }),
        });
        const raced = g !== retierGen; // user acted while our POST was in flight
        if (res.ok) {
          const data = await res.json();
          if (!raced) dispatchCartUpdate(data.sections, RETIER_SOURCE);
          else await repairIntent(sections); // we may have clobbered their qty
        } else {
          failed = true;
          failedModes.push(modeKey(line));
          console.error('[LoopRangeBundle] txn rewrite rejected', res.status);
        }
      } catch (e) {
        failed = true;
        failedModes.push(modeKey(line));
        console.error('[LoopRangeBundle] txn rewrite failed', e);
      }
    }
    if (g !== retierGen) return;
    snapshot(cart);
    if (failed && retierRetries < 3) {
      retierRetries++;
      const s = readSnapshot();
      for (const m of failedModes) s[m] = -1;
      try {
        sessionStorage.setItem(SNAP_KEY, JSON.stringify(s));
      } catch (e) {
        /* ignore */
      }
      scheduleTxnSync(retierGen);
    } else if (!failed) {
      retierRetries = 0;
    }
  }

  /**
   * The user's last stepper action is the source of truth for quantity. If our
   * txn rewrite raced their click and won server-side, restore what they asked.
   * ponytail: single-bundle-line carts only (the overwhelmingly common case);
   * multi-line raced carts self-correct on the next sync cycle.
   */
  async function repairIntent(sections) {
    if (!lastIntent || Date.now() - lastIntent.at > 10000 || !(lastIntent.qty > 0)) return;
    const lines = bundleLines(await getCart());
    if (lines.length !== 1 || lines[0].quantity === lastIntent.qty) return;
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lines[0].key,
        quantity: lastIntent.qty,
        sections: sections,
        sections_url: window.location.pathname,
      }),
    });
    if (res.ok) {
      dispatchCartUpdate((await res.json()).sections, RETIER_SOURCE);
      scheduleTxnSync(retierGen); // restored qty needs its own txn/code pass
    }
  }

  // The guard must work on EVERY page (the drawer is global): when details aren't
  // initialized yet, bootstrap the bundle id from the cart line's own
  // `_loopRangeBundleNanoId` property — no PDP context needed.
  async function ensureDetailsFromCart() {
    if (details || Object.keys(fixedDetails).length) return true;
    const cart = await getCart();
    const marked = cart.items.find((i) => i.properties && i.properties._loopRangeBundleNanoId);
    if (marked) {
      await init(marked.properties._loopRangeBundleNanoId);
      return true;
    }
    // Fixed-box lines must ARM the guard too — retier owns their code
    // registration and the all-or-nothing sweep (found by matrix S1.6).
    const fixed = cart.items.find((i) => i.properties && i.properties._loopFixedBundleNanoId);
    if (fixed) {
      await initFixed(fixed.properties._loopFixedBundleNanoId).catch(() => {});
      return true;
    }
    return false;
  }

  // Any stepper click invalidates in-flight guard cycles IMMEDIATELY (the
  // click's own cart:update only lands ~1s later) — see retierGen above. Also
  // capture the user's intended quantity for repairIntent(): prefer the open
  // drawer's bundle row input, fall back to the first on the page.
  document.addEventListener('quantity-selector:update', () => {
    retierGen++;
    clearTimeout(retierTimer);
    clearTimeout(txnTimer);
    const dialog = document.querySelector('dialog.cart-drawer__dialog[open]');
    const scope = dialog || document;
    const input = scope.querySelector('.cart-items__table-row[data-bundle-line] input[name="quantity"], .cart-items__table-row[data-bundle-line] input[type="number"]');
    if (input && Number(input.value) > 0) lastIntent = { qty: Number(input.value), at: Date.now() };
  });

  document.addEventListener('cart:update', (e) => {
    const src = e && e.detail && e.detail.data && e.detail.data.source;
    if (src === RETIER_SOURCE) return; // our own rewrite — don't loop
    const resource = e && e.detail && e.detail.resource;
    const seed = resource && Array.isArray(resource.items) ? resource : null;
    const g = ++retierGen;
    clearTimeout(retierTimer);
    clearTimeout(txnTimer);
    // 150ms: just enough to coalesce rapid stepping (the native stepper already
    // debounces 300ms) — was 800ms, which made the price correction feel slow.
    // The generation guard (not the debounce) is what prevents interleaving
    // guard writes with the user's own actions.
    // ponytail: sub-second correction would need firing the discount write at
    // click time in parallel with the native change — race-prone, not worth it.
    retierTimer = setTimeout(() => {
      ensureDetailsFromCart()
        .then((armed) => (armed ? retier(seed, g) : null))
        .catch((err) => console.error('[LoopRangeBundle] retier', err));
    }, 150);
  });

  // Boot heal: a page can LOAD into a stale session (checkout cookie holds an old
  // tier code while the cart quantity moved — seen live 17 Jul: qty 13 billed 25%).
  // The guard above only fires on cart:update, so run one retier pass after load.
  // Gated on a server-rendered bundle row: zero cost for visitors without one.
  function bootHeal() {
    if (!document.querySelector('[data-bundle-line]')) return;
    ensureDetailsFromCart()
      .then((armed) => (armed ? retier() : null))
      .catch(() => {});
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(bootHeal, 800));
  } else {
    setTimeout(bootHeal, 800);
  }
  // Returning from checkout via back/forward-cache: a checkout-side upsell may
  // have rewritten the cart while no storefront JS was running — re-heal.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) setTimeout(bootHeal, 300);
  });

  // ---- Checkout money gate --------------------------------------------------
  // Opening checkout PINS the cart's discount-code set server-side. A third-
  // party cart write (GWP auto-apply, upsell app) can evict the tier codes in
  // the window between our last registration and the click — the customer
  // would pay the un-tiered price (23 Jul: drawer $184.68, checkout $205.20).
  // Re-register against the REAL cart before the navigation proceeds.
  // Fail-open after 1500ms: never block a checkout over a slow/failed sync.
  let gatePassed = false;
  function gateArmed() {
    return !!(
      details ||
      Object.keys(fixedDetails).length ||
      sessionStorage.getItem(CODES_KEY) ||
      document.querySelector('[data-bundle-line]')
    );
  }
  function gateSync() {
    return Promise.race([
      ensureDetailsFromCart()
        .then(getCart)
        .then((cart) => registerCodes(cart)),
      new Promise((r) => setTimeout(r, 1500)),
    ]).catch(() => {});
  }
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      const isCheckout =
        (e.submitter && e.submitter.name === 'checkout') ||
        /\/checkout/.test(form.getAttribute('action') || '');
      if (!isCheckout || !gateArmed()) return;
      if (gatePassed) {
        gatePassed = false; // second pass after sync — let it through
        return;
      }
      if (!form.requestSubmit) return; // no re-submit API -> fail open
      e.preventDefault();
      const submitter = e.submitter;
      gateSync().then(() => {
        gatePassed = true;
        form.requestSubmit(submitter || undefined);
      });
    },
    true
  );
  document.addEventListener(
    'click',
    (e) => {
      const a = e.target && e.target.closest && e.target.closest('a[href^="/checkout"]');
      if (!a || !gateArmed()) return;
      e.preventDefault();
      gateSync().then(() => {
        window.location.href = a.href;
      });
    },
    true
  );

  window.LoopRangeBundle = {
    init: init,
    addToCart: addToCart,
    addFixedToCart: addFixedToCart,
    initFixed: initFixed,
    flipPlan: flipPlan,
    tierFor: tierFor,
    retier: retier,
  };
})();
